<script lang="ts">
  import Close from '~icons/mdi/close';
  import type { HighlightColors, Tile } from '~/types';
  import Star from '~/icons/Star.svelte';
  import { getDimensions } from '~/utils/DimensionsContext.svelte';

  const dims = getDimensions();
  type Props = {
    width?: number | undefined;
    tile: Tile;
    selected?: boolean;
    hovered?: boolean;
    dragging?: boolean;
    flying?: boolean;
    highlighted?: HighlightColors;
    size?:
      | 'logo'
      | 'splash'
      | 'drawer'
      | 'small'
      | 'board'
      | 'share'
      | 'classic-share';
  };
  const props: Props = $props();
</script>

<div
  class="tile {props.size} {props.highlighted}"
  class:selected={props.selected}
  class:hovered={props.hovered}
  class:dragging={props.dragging}
  class:flying={props.flying}
  class:penalty={props.tile.points && props.tile.points < 0}
  style="
    --tile-width: {props.width ?? dims.board?.width / 12}px;
  "
>
  <span>{props.tile.letter}</span>
  {#if props.tile.points !== undefined && props.tile.points !== 0}
    <span class="score">{props.tile.points}</span>
  {/if}
  {#if (props.tile.multiplier ?? 0) > 1}
    <span class="multiplier mult-{props.tile.multiplier}">
      {props.tile.multiplier}
      <Close size="0.5em" />
    </span>
  {/if}
  {#if props.tile.star}
    <Star class="yellow tile outlined" size="40%" />
  {/if}
</div>

<style lang="scss">
  @use 'sass:list';
  @use 'sass:color';
  @use '~/theme/theme';
  @use '~/theme/themify' as *;
  @use '~/theme/include-media' as *;

  $bg: #f5f6fa;
  $dark-bg: #2f3640;

  .tile {
    box-sizing: border-box;
    z-index: 500;
    position: relative;
    transition:
      background-color 0.2s ease,
      border 0.2s ease,
      color 0.2s ease,
      transform 0.3s 0.1s ease;
    border: 1px solid theme.$tile-blue-light;
    border-bottom: 2px solid theme.$tile-blue-light;
    border-radius: 10%;
    width: 100%;
    height: 100%;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    cursor: pointer;
    &.share {
      width: 48px;
      height: 48px;
      font-size: 28px;
      border-width: 2px;
      border-bottom-width: 6px;
    }
    &.classic-share {
      width: 108px;
      height: 108px;
      font-size: 56px;
      border-width: 4px;
      border-bottom-width: 8px;
    }
    &.small {
      width: var(--tile-width);
      height: var(--tile-width);
      font-size: calc(var(--tile-width) / 2);
    }
    &.logo,
    &.splash,
    &.drawer {
      cursor: initial;
      & .score {
        display: none;
      }
    }
    &.logo {
      font-size: 1rem;
      width: 1.5em;
      height: 1.5em;
    }
    &.splash {
      width: 1.5em;
      height: 1.5em;
      font-size: 1.5em;
    }
    &.drawer {
      width: 1.5em;
      height: 1.5em;
      font-size: 0.875rem;
      border-width: 1.5px 1.5px 2px 1.5px;
    }
    &.dragging {
      transform: scale(1.125);
      transition: transform 0.2 ease 0.2;
    }
    &.selected {
      transform: scale(1.125);
      transition: transform 0.2 ease;
    }
    @include themify {
      background-color: themed('tile-default');
      &.blue,
      &.hovered,
      &.selected {
        @include theme.tile-color(themed('tile-blue'));
      }
      &.green {
        @include theme.tile-color(themed('tile-green'));
      }
      &.purple {
        @include theme.tile-color(themed('tile-purple'));
      }
      &.red,
      &.penalty {
        @include theme.tile-color(themed('tile-red'));
      }
      &.orange {
        @include theme.tile-color(themed('tile-orange'));
      }
      &.daily-vertical {
        color: black;
        @include theme.tile-color(themed('daily-vertical'));
      }
      &.daily-horizontal {
        color: black;
        @include theme.tile-color(themed('daily-horizontal'));
      }
    }
    & .score {
      position: absolute;
      font-size: 0.5em;
      font-weight: normal;
      opacity: 0.8;
      bottom: 0;
      right: 0.4em;
    }
    & .debug-id {
      position: absolute;
      opacity: 0;
      font-size: 0.5em;
      font-weight: normal;
      top: 0;
      right: 0;
      padding: 0.2em;
    }
    & .multiplier {
      position: absolute;
      height: 1.875em;
      width: 1.875em;
      top: 0;
      left: 0;
      font-size: 0.625em;
      line-height: 1;
      padding: 0.125em;
      font-weight: normal;
      border-radius: calc(0.1 * var(--tile-width)) 0 0 0;
      display: flex;
      align-items: start;
      justify-content: start;

      $colors: theme.$multiplier-2-light theme.$multiplier-3-light
        theme.$multiplier-4-light;

      @for $i from 2 through 4 {
        $color: list.nth($colors, $i - 1);
        &.mult-#{$i} {
          background: linear-gradient(
            to bottom right,
            $color 0%,
            $color 50%,
            #ffffff00 50%,
            #ffffff00 100%
          );
        }
      }
    }
    :global(html.dark-mode) & {
      border-color: black;
      $dark-colors: theme.$multiplier-2-dark theme.$multiplier-3-dark
        theme.$multiplier-4-dark;

      @for $i from 2 through 4 {
        $dark-color: list.nth($dark-colors, $i - 1);
        & .mult-#{$i} {
          background: linear-gradient(
            to bottom right,
            $dark-color 0%,
            $dark-color 50%,
            #ffffff00 50%,
            #ffffff00 100%
          );
        }
      }
    }
  }
</style>
